import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Trash2, Pencil, Check, X, ArrowUp, ArrowDown,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiPost, apiPut } from '../../../api/client';
import { Button } from '../../../components/ui/Button';
import type { PackageItem, ServiceType, ProductTemplate } from '../../../api/types';
import { OptionsPanel } from './OptionsPanel';
import { AttributeValuesEditor } from './AttributeValuesEditor';
import {
  SERVICE_TYPES,
  svcEmoji, marginPct, marginColor,
} from './constants';
import { inpSt, selSt, iconBtnStyle } from './sharedStyles';
import { renderCatalogIcon } from '../../../utils/renderCatalogIcon.js';
import { resolveServiceType, resolveCategoryIcon } from '../../../utils/categoryMapping';
import { useProductCategories } from '../../../api/useProductCategories';

// ─── ItemRow ──────────────────────────────────────────────────────────────────

export function ItemRow({
  item, isFirst, isLast, packageId, templates,
  onMoveUp, onMoveDown, onDelete,
}: {
  item: PackageItem; isFirst: boolean; isLast: boolean; packageId: string;
  templates: ProductTemplate[];
  onMoveUp: () => void; onMoveDown: () => void; onDelete: () => void;
}) {
  const qc = useQueryClient();
  const { data: categories = [] } = useProductCategories();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    serviceType: item.serviceType,
    label: item.label,
    basePrice: String(item.basePrice),
    costOverride: item.costOverride != null ? String(item.costOverride) : '',
    priceOverride: item.priceOverride != null ? String(item.priceOverride) : '',
    options: (item.options as Record<string, unknown>) ?? {},
    attributeValues: (item.attributeValues as Record<string, unknown>) ?? {},
  });

  const linkedTemplate = item.productTemplateId
    ? templates.find(t => t.id === item.productTemplateId)
    : null;

  const effectiveCost = item.costOverride ?? linkedTemplate?.baseCost ?? 0;
  const effectivePrice = item.priceOverride ?? item.basePrice;
  const mPct = marginPct(effectiveCost, effectivePrice);

  const updateMutation = useMutation({
    mutationFn: () => apiPut(`/service-packages/${packageId}/items/${item.id}`, {
      serviceType: form.serviceType,
      label: form.label,
      basePrice: Number(form.basePrice) || 0,
      costOverride: form.costOverride !== '' ? Number(form.costOverride) : null,
      priceOverride: form.priceOverride !== '' ? Number(form.priceOverride) : null,
      options: Object.keys(form.options).length ? form.options : null,
      attributeValues: Object.keys(form.attributeValues).length ? form.attributeValues : null,
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['service-packages'] }); setEditing(false); },
    onError: (e: Error) => toast.error(e.message),
  });

  if (!editing) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        padding: '0.75rem 1rem',
        borderRadius: 'var(--radius-card)',
        background: 'var(--glass-bg-card)',
        border: '1px solid rgba(255,255,255,0.10)',
        transition: 'all 0.15s ease',
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <button onClick={onMoveUp} disabled={isFirst} style={{ ...iconBtnStyle, opacity: isFirst ? 0.3 : 0.6, transition: 'opacity 0.15s' }} onMouseEnter={(e) => !isFirst && (e.currentTarget.style.opacity = '1')} onMouseLeave={(e) => !isFirst && (e.currentTarget.style.opacity = '0.6')}><ArrowUp size={13} /></button>
          <button onClick={onMoveDown} disabled={isLast} style={{ ...iconBtnStyle, opacity: isLast ? 0.3 : 0.6, transition: 'opacity 0.15s' }} onMouseEnter={(e) => !isLast && (e.currentTarget.style.opacity = '1')} onMouseLeave={(e) => !isLast && (e.currentTarget.style.opacity = '0.6')}><ArrowDown size={13} /></button>
        </div>
        {linkedTemplate ? (
          <span style={{ fontSize: '1.1rem', flexShrink: 0, display: 'flex', alignItems: 'center', color: 'var(--color-text-muted)' }}>
            {linkedTemplate.icon ? renderCatalogIcon(linkedTemplate.icon, 18) : renderCatalogIcon(resolveCategoryIcon(linkedTemplate, categories), 18)}
          </span>
        ) : (
          <span style={{ fontSize: '1.1rem', flexShrink: 0 }}>{svcEmoji(item.serviceType)}</span>
        )}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '0.125rem' }}>
          <span style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--color-text)' }}>{item.label}</span>
          {linkedTemplate ? (
            <>
              <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)' }}>
                {linkedTemplate.name}
              </span>
              {item.attributeValues && Object.keys(item.attributeValues as Record<string, unknown>).length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', marginTop: '0.125rem' }}>
                  {Object.entries(item.attributeValues as Record<string, unknown>).map(([k, v]) =>
                    v !== '' && v != null ? (
                      <span key={k} style={{
                        fontSize: '10px', padding: '0.0625rem 0.375rem',
                        background: 'rgba(255,255,255,0.02)',
                        border: '1px solid rgba(255,255,255,0.10)',
                        borderRadius: 'var(--radius-interactive)',
                        color: 'var(--color-text-muted)',
                      }}>
                        {k}: {String(v)}
                      </span>
                    ) : null
                  )}
                </div>
              )}
            </>
          ) : (
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.25rem',
              fontSize: 'var(--text-xs)',
              color: 'var(--color-error)',
              background: 'oklch(from var(--color-error) l c h / 0.12)',
              padding: '0.25rem 0.5rem',
              borderRadius: 'var(--radius-interactive)',
              width: 'fit-content',
            }}
            title="Questo servizio non è collegato a nessun prodotto del catalogo">
              ⚠ Non in catalogo
            </span>
          )}
        </div>
        {linkedTemplate && effectiveCost > 0 && (
          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>
            costo € {effectiveCost.toFixed(2)}
          </span>
        )}
        <span style={{ fontSize: 'var(--text-sm)', fontWeight: 700, color: 'var(--color-primary)', whiteSpace: 'nowrap' }}>
          € {effectivePrice.toFixed(2)}
        </span>
        {linkedTemplate && effectiveCost > 0 && (
          <span style={{
            fontSize: 'var(--text-xs)',
            fontWeight: 700,
            color: marginColor(mPct),
            whiteSpace: 'nowrap',
            background: marginColor(mPct) === 'var(--color-success)' ? 'oklch(from var(--color-success) l c h / 0.12)' : marginColor(mPct) === 'var(--color-warning)' ? 'oklch(from var(--color-warning) l c h / 0.12)' : 'oklch(from var(--color-error) l c h / 0.12)',
            padding: '0.25rem 0.5rem',
            borderRadius: 'var(--radius-interactive)',
          }}>
            {mPct}%
          </span>
        )}
        <button
          onClick={() => {
            setForm({
              serviceType: item.serviceType,
              label: item.label,
              basePrice: String(item.basePrice),
              costOverride: item.costOverride != null ? String(item.costOverride) : '',
              priceOverride: item.priceOverride != null ? String(item.priceOverride) : '',
              options: (item.options as Record<string, unknown>) ?? {},
              attributeValues: (item.attributeValues as Record<string, unknown>) ?? {},
            });
            setEditing(true);
          }}
          style={{ ...iconBtnStyle, opacity: 0.6, transition: 'opacity 0.15s' }}
          onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.6')}
          title="Modifica"
        >
          <Pencil size={14} />
        </button>
        <button
          onClick={onDelete}
          style={{ ...iconBtnStyle, color: 'var(--color-error)', opacity: 0.6, transition: 'opacity 0.15s' }}
          onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.6')}
          title="Elimina"
        >
          <Trash2 size={14} />
        </button>
      </div>
    );
  }

  // Edit mode
  const editLinked = item.productTemplateId ? templates.find(t => t.id === item.productTemplateId) : null;

  return (
    <div style={{ padding: '0.625rem', borderRadius: 'var(--radius-interactive)', background: 'var(--glass-bg-card)', border: '1px solid var(--color-primary)' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 100px', gap: '0.5rem' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          <label style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', fontWeight: 500 }}>Tipo</label>
          {editLinked ? (
            <input value={editLinked.name} readOnly style={{ ...inpSt, color: 'var(--color-text-faint)' }} />
          ) : (
            <select value={form.serviceType} onChange={e => setForm(f => ({ ...f, serviceType: e.target.value as ServiceType, options: {} }))} style={selSt}>
              {SERVICE_TYPES.map(s => <option key={s.value} value={s.value}>{s.emoji} {s.label}</option>)}
            </select>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          <label style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', fontWeight: 500 }}>Label</label>
          <input value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))} style={inpSt} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          <label style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', fontWeight: 500 }}>Prezzo €</label>
          <input type="number" min="0" step="0.01"
            value={form.priceOverride !== '' ? form.priceOverride : form.basePrice}
            placeholder={editLinked ? String(editLinked.basePrice) : '0'}
            onChange={e => {
              setForm(f => ({
                ...f,
                priceOverride: e.target.value,
                basePrice: e.target.value,
              }));
            }}
            style={inpSt}
          />
        </div>
      </div>
      {editLinked && (
        <div style={{ marginTop: '0.5rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <label style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', fontWeight: 500 }}>
                Costo override € <span style={{ fontWeight: 400, color: 'var(--color-text-faint)' }}>(default: {editLinked.baseCost.toFixed(2)})</span>
              </label>
              <input type="number" min="0" step="0.01"
                value={form.costOverride}
                placeholder={String(editLinked.baseCost)}
                onChange={e => setForm(f => ({ ...f, costOverride: e.target.value }))}
                style={inpSt}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '0.1rem' }}>
              {(() => {
                const c = form.costOverride !== '' ? Number(form.costOverride) : editLinked.baseCost;
                const p = form.priceOverride !== '' ? Number(form.priceOverride) : (form.basePrice !== '' ? Number(form.basePrice) : editLinked.basePrice);
                const m = marginPct(c, p);
                return (
                  <span style={{ fontSize: 'var(--text-sm)', fontWeight: 700, color: marginColor(m) }}>
                    Margine: {m}%
                  </span>
                );
              })()}
            </div>
          </div>
          <AttributeValuesEditor
            template={editLinked}
            values={form.attributeValues}
            onChange={av => setForm(f => ({ ...f, attributeValues: av }))}
          />
        </div>
      )}
      {!editLinked && (
        <OptionsPanel serviceType={form.serviceType} options={form.options} onChange={opts => setForm(f => ({ ...f, options: opts }))} />
      )}
      <div style={{ display: 'flex', gap: '0.375rem', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
        <button onClick={() => setEditing(false)} style={iconBtnStyle}><X size={14} color="var(--color-text-muted)" /></button>
        <button onClick={() => updateMutation.mutate()} style={iconBtnStyle} disabled={updateMutation.isPending}><Check size={14} color="var(--color-success)" /></button>
      </div>
    </div>
  );
}

// ─── AddItemRow ───────────────────────────────────────────────────────────────

export function AddItemRow({
  packageId, sortOrder, templates, onDone,
}: {
  packageId: string; sortOrder: number; templates: ProductTemplate[]; onDone: () => void;
}) {
  const qc = useQueryClient();
  const { data: categories = [] } = useProductCategories();
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [catalogForm, setCatalogForm] = useState({
    label: '',
    priceOverride: '',
    costOverride: '',
    attributeValues: {} as Record<string, unknown>,
  });

  const selectedTemplate = templates.find(t => t.id === selectedTemplateId) ?? null;

  const handleTemplateChange = (id: string) => {
    setSelectedTemplateId(id);
    const tpl = templates.find(t => t.id === id);
    if (tpl) {
      const defaultAttrValues: Record<string, unknown> = {};
      tpl.attributes?.forEach(a => {
        if (a.defaultValue != null) defaultAttrValues[a.key] = a.defaultValue;
      });
      setCatalogForm(f => ({
        ...f,
        label: tpl.name,
        priceOverride: '',
        costOverride: '',
        attributeValues: defaultAttrValues,
      }));
    }
  };

  const addMutation = useMutation({
    mutationFn: () => {
      if (selectedTemplate) {
        return apiPost(`/service-packages/${packageId}/items`, {
          serviceType: resolveServiceType(selectedTemplate, categories),
          label: catalogForm.label || selectedTemplate.name,
          basePrice: catalogForm.priceOverride !== '' ? Number(catalogForm.priceOverride) : selectedTemplate.basePrice,
          costOverride: catalogForm.costOverride !== '' ? Number(catalogForm.costOverride) : null,
          priceOverride: catalogForm.priceOverride !== '' ? Number(catalogForm.priceOverride) : null,
          productTemplateId: selectedTemplate.id,
          attributeValues: Object.keys(catalogForm.attributeValues).length ? catalogForm.attributeValues : null,
          sortOrder,
        });
      }
      return Promise.reject(new Error('Seleziona un prodotto'));
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['service-packages'] });
      onDone();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const catalogValid = !!selectedTemplateId && !!catalogForm.label.trim();

  return (
    <div style={{ marginTop: '0.25rem', padding: '0.75rem', background: 'var(--glass-bg-card)', borderRadius: 'var(--radius-interactive)', border: '1px dashed var(--color-primary)' }}>
      {templates.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '1rem' }}>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-faint)', marginBottom: '0.5rem' }}>
            Aggiungi prima prodotti al Catalogo
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <div>
            <label style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', fontWeight: 500 }}>Prodotto dal catalogo *</label>
            <select
              value={selectedTemplateId}
              onChange={e => handleTemplateChange(e.target.value)}
              style={{ ...selSt, marginTop: '0.25rem' }}
            >
              <option value="">— seleziona prodotto —</option>
              {templates.filter(t => t.isActive).map(t => (
                <option key={t.id} value={t.id}>
                  {renderCatalogIcon(resolveCategoryIcon(t, categories), 18)} {t.name} (€ {t.basePrice.toFixed(2)})
                </option>
              ))}
            </select>
          </div>

          {selectedTemplate && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                <div>
                  <label style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', fontWeight: 500 }}>Label nel pacchetto</label>
                  <input
                    value={catalogForm.label}
                    onChange={e => setCatalogForm(f => ({ ...f, label: e.target.value }))}
                    style={{ ...inpSt, marginTop: '0.25rem' }}
                    placeholder={selectedTemplate.name}
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                  <div>
                    <label style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', fontWeight: 500 }}>
                      Prezzo € <span style={{ fontWeight: 400, color: 'var(--color-text-faint)' }}>(def: {selectedTemplate.basePrice.toFixed(2)})</span>
                    </label>
                    <input type="number" min="0" step="0.01"
                      value={catalogForm.priceOverride}
                      placeholder={String(selectedTemplate.basePrice)}
                      onChange={e => setCatalogForm(f => ({ ...f, priceOverride: e.target.value }))}
                      style={{ ...inpSt, marginTop: '0.25rem' }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', fontWeight: 500 }}>
                      Costo € <span style={{ fontWeight: 400, color: 'var(--color-text-faint)' }}>(def: {selectedTemplate.baseCost.toFixed(2)})</span>
                    </label>
                    <input type="number" min="0" step="0.01"
                      value={catalogForm.costOverride}
                      placeholder={String(selectedTemplate.baseCost)}
                      onChange={e => setCatalogForm(f => ({ ...f, costOverride: e.target.value }))}
                      style={{ ...inpSt, marginTop: '0.25rem' }}
                    />
                  </div>
                </div>
              </div>

              {(() => {
                const c = catalogForm.costOverride !== '' ? Number(catalogForm.costOverride) : selectedTemplate.baseCost;
                const p = catalogForm.priceOverride !== '' ? Number(catalogForm.priceOverride) : selectedTemplate.basePrice;
                const m = marginPct(c, p);
                return (
                  <div style={{ display: 'flex', gap: '1rem', fontSize: 'var(--text-xs)' }}>
                    <span style={{ color: 'var(--color-text-muted)' }}>Costo: <strong>€ {c.toFixed(2)}</strong></span>
                    <span style={{ color: 'var(--color-text-muted)' }}>Prezzo: <strong>€ {p.toFixed(2)}</strong></span>
                    <span style={{ fontWeight: 700, color: marginColor(m) }}>Margine: {m}%</span>
                  </div>
                );
              })()}

              <AttributeValuesEditor
                template={selectedTemplate}
                values={catalogForm.attributeValues}
                onChange={av => setCatalogForm(f => ({ ...f, attributeValues: av }))}
              />
            </>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.375rem', marginTop: '0.5rem' }}>
        <Button size="sm" onClick={() => addMutation.mutate()} loading={addMutation.isPending}
          disabled={templates.length === 0 || !catalogValid} icon={<Check size={12} />}>
          Aggiungi al pacchetto
        </Button>
        <Button size="sm" variant="ghost" onClick={onDone}>Annulla</Button>
      </div>
    </div>
  );
}
